import { Check } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { scrollTo } from "./Navbar";

const pathways = [
  {
    tag: "Foundation",
    who: "For new & aspiring traders",
    title: "Build the base",
    outcomes: [
      "Understand market structure & risk",
      "Install a repeatable daily routine",
      "Develop the trader's mindset",
    ],
    expects: "Self paced study · core curriculum",
    featured: false,
  },
  {
    tag: "Advanced",
    who: "For traders with some experience",
    title: "Sharpen the edge",
    outcomes: [
      "Refine your trading and decision making",
      "Live sessions & trade reviews",
      "Psychology under real pressure",
    ],
    expects: "Cohort + live sessions · accountability",
    featured: true,
  },
  {
    tag: "Performance",
    who: "For committed, funded traders",
    title: "Trade at a standard",
    outcomes: [
      "1:1 mentorship & funded roadmap",
      "Discipline as you scale up",
      "Consistency measured over time",
    ],
    expects: "High commitment · direct access",
    featured: false,
  },
];

export default function Academy() {
  return (
    <section
      id="academy"
      className="relative py-24 sm:py-32 lg:py-40 border-t border-white/5"
      data-testid="academy-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <SectionLabel>The Academy</SectionLabel>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-2xl">
            Three pathways. One standard.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 text-sm text-zinc-500 font-light max-w-xl">
            A clear progression, admission is by application. Every pathway is
            built around real skill, not shortcuts.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-[#9fd3ff]/30 bg-[#9fd3ff]/[0.06] px-4 py-2" data-testid="academy-intake-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-[#7ee0a1]" style={{ boxShadow: "0 0 8px #7ee0a1" }} />
            <span className="section-label text-[10px] text-[#9fd3ff]">Next intake — Courses start August 2026</span>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {pathways.map((p, i) => (
            <Reveal key={p.tag} delay={i * 0.12}>
              <div
                className={`h-full flex flex-col p-8 border transition-colors duration-500 ${
                  p.featured ? "bg-[#101010] border-white/25" : "bg-[#0F0F0F] border-white/5 hover:border-white/20"
                }`}
                data-testid={`academy-card-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="section-label text-[10px] text-[#9fd3ff]">{p.tag}</span>
                  {p.featured && (
                    <span className="section-label text-[9px] text-[#0A0A0A] bg-[#D3D3D3] px-2 py-1">
                      Flagship
                    </span>
                  )}
                </div>
                <div className="mt-3 text-xs text-zinc-500 font-light">{p.who}</div>
                <h3 className="mt-4 font-heading text-2xl font-light">{p.title}</h3>

                <div className="mt-6 section-label text-[9px] text-zinc-600">Outcomes</div>
                <ul className="mt-3 space-y-3 flex-1">
                  {p.outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-3">
                      <Check strokeWidth={1.25} className="h-4 w-4 text-[#6ea8ff] mt-0.5 shrink-0" />
                      <span className="text-sm text-zinc-300 font-light">{o}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="section-label text-[9px] text-zinc-600">Expectations</div>
                  <div className="mt-2 text-sm text-zinc-300 font-light">{p.expects}</div>
                  <button
                    onClick={() => scrollTo("apply")}
                    className={`mt-6 w-full py-3.5 text-sm font-medium tracking-wide transition-colors duration-300 ${
                      p.featured ? "btn-solid" : "btn-glass text-white"
                    }`}
                    data-testid={`academy-apply-${i}`}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
